import { useMemo, useState } from 'react';
import { Task } from '../../../types';
/**
 * Verifica se uma tarefa corresponde aos filtros de status/prazo selecionados.
 * Aplica lógica de "arquivamento":
 * - Se nenhum filtro selecionado: mostra apenas pendentes (!isCompleted).
 * - Se filtros selecionados: aplica lógica OR, mas tarefas concluídas só aparecem
 *   se o filtro 'completed' estiver explicitamente selecionado.
 */
export const checkTaskStatusMatch = (task: Task, statusFilter: string[]) => {
    if (statusFilter.length === 0) {
        // Default: Mostra apenas tarefas não concluídas (pendentes)
        // Arquivando as concluídas
        return !task.isCompleted;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let taskDate: Date | null = null;
    if (task.dueDate) {
        taskDate = new Date(task.dueDate);
        taskDate.setHours(0, 0, 0, 0);
    }

    return statusFilter.some(filter => {
        if (filter === 'completed') return task.isCompleted;
        if (task.isCompleted) return false;

        if (filter === 'overdue') {
            return taskDate && taskDate < today;
        }

        if (filter === 'today') {
            return taskDate && taskDate.getTime() === today.getTime();
        }

        if (filter === 'this_week') {
            const currentDay = today.getDay();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - currentDay);

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            endOfWeek.setHours(23, 59, 59, 999);

            return taskDate && taskDate >= startOfWeek && taskDate <= endOfWeek;
        }

        if (filter === 'next_week') {
            const currentDay = today.getDay();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - currentDay);

            const startNext = new Date(startOfWeek);
            startNext.setDate(startOfWeek.getDate() + 7);

            const endNext = new Date(startNext);
            endNext.setDate(startNext.getDate() + 6);
            endNext.setHours(23, 59, 59, 999);

            return taskDate && taskDate >= startNext && taskDate <= endNext;
        }

        return false;
    });
};

/**
 * Hook para filtrar uma lista de tarefas por status/prazo.
 */
export const useTaskFiltering = (tasks: Task[]) => {
    const [statusFilter, setStatusFilter] = useState<string[]>([]);

    const filteredTasks = useMemo(() => {
        return tasks.filter(task => checkTaskStatusMatch(task, statusFilter));
    }, [tasks, statusFilter]);

    return {
        statusFilter,
        setStatusFilter,
        filteredTasks
    };
};
